import { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  DollarSign, TrendingUp, TrendingDown, Users,
  AlertTriangle, RefreshCw, Clock, CheckCircle,
  XCircle, FileText, CreditCard, Wallet, Shield,
  BarChart2, Activity, UserCheck, UserX
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) =>
  n != null ? Number(n).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : '0';

const fmtUSD = (n) => '$' + fmt(n, 2);

const METHOD_LABELS = {
  bank_transfer_global:      'Bank Transfer',
  bank_transfer_per_user:    'Bank (Per User)',
  bank_transfer_per_country: 'Bank (Per Country)',
  crypto_payments:           'Crypto',
  payment_links:             'Payment Link',
  bank:                      'Bank Transfer',
};
const methodLabel = (m) => METHOD_LABELS[m] || (m ? m.replace(/_/g, ' ') : '—');

const hourLabel = (h) => {
  if (h < 1) return 'Just now';
  if (h === 1) return '1 hr ago';
  return `${h} hrs ago`;
};

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold font-mono mb-0.5" style={{ color }}>{value}</p>
      {subtext && <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{subtext}</p>}
    </div>
  );
}

// ─── Metric card ──────────────────────────────────────────────────────────────
function MetricCard({ label, value, icon: Icon, color = 'var(--text-primary)', subtext }) {
  return (
    <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        <Icon className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
      </div>
      <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
      {subtext && <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{subtext}</p>}
    </div>
  );
}

// ─── Section heading ──────────────────────────────────────────────────────────
function SectionHeading({ icon: Icon, title, color = 'var(--accent-primary)' }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4" style={{ color }} />
      <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
    </div>
  );
}

// ─── Pending request row ──────────────────────────────────────────────────────
function PendingRow({ r }) {
  const isDeposit = r.request_type === 'deposit';
  const urgent = r.hours_pending >= 2;
  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-0"
      style={{ borderColor: 'var(--border-color)' }}>
      {/* Type icon */}
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: isDeposit
            ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)'
            : 'color-mix(in srgb, var(--accent-warning) 15%, transparent)',
        }}>
        {isDeposit
          ? <TrendingUp className="w-3.5 h-3.5" style={{ color: 'var(--accent-success)' }} />
          : <TrendingDown className="w-3.5 h-3.5" style={{ color: 'var(--accent-warning)' }} />}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
            {r.user_name || r.email}
          </p>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded capitalize"
            style={{
              backgroundColor: isDeposit
                ? 'color-mix(in srgb, var(--accent-success) 12%, transparent)'
                : 'color-mix(in srgb, var(--accent-warning) 12%, transparent)',
              color: isDeposit ? 'var(--accent-success)' : 'var(--accent-warning)',
            }}>
            {r.request_type}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{methodLabel(r.method)}</p>
          <span style={{ color: 'var(--text-tertiary)' }}>·</span>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>#{r.id}</p>
          {r.has_proof === 1 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-info) 12%, transparent)', color: 'var(--accent-info)' }}>
              Proof submitted
            </span>
          )}
        </div>
      </div>

      {/* Amount + time */}
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
          {fmtUSD(r.amount)}
        </p>
        <p className="text-[10px] flex items-center gap-1 justify-end mt-0.5"
          style={{ color: urgent ? 'var(--accent-danger)' : 'var(--text-tertiary)' }}>
          <Clock className="w-2.5 h-2.5" />
          {hourLabel(r.hours_pending)}
        </p>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Reports() {
  const [loading,          setLoading]          = useState(true);
  const [dateRange,        setDateRange]        = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date:   new Date().toISOString().split('T')[0],
  });
  const [reportData,       setReportData]       = useState(null);
  const [riskyAccounts,    setRiskyAccounts]    = useState([]);
  const [pendingRequests,  setPendingRequests]  = useState([]);

  useEffect(() => { loadReports(); }, [dateRange]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [dashboard, risky, pending] = await Promise.all([
        reportsAPI.getDashboard(dateRange),
        reportsAPI.getRiskyAccounts(10),
        reportsAPI.getPendingRequests(),
      ]);
      setReportData(dashboard.data);
      setRiskyAccounts(risky.data || []);
      setPendingRequests(pending.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  const { financial, trading, users, top_symbols } = reportData || {};
  const deposits    = financial?.deposits    || {};
  const withdrawals = financial?.withdrawals || {};

  // Pending split
  const pendingDeposits    = pendingRequests.filter(r => r.request_type === 'deposit');
  const pendingWithdrawals = pendingRequests.filter(r => r.request_type === 'withdrawal');
  const urgentCount        = pendingRequests.filter(r => r.hours_pending >= 2).length;

  return (
    <div className="space-y-8 p-6 max-w-6xl mx-auto">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Reports & Analytics</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>Comprehensive broker performance metrics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={dateRange.start_date}
            onChange={e => setDateRange(p => ({ ...p, start_date: e.target.value }))}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>to</span>
          <input type="date" value={dateRange.end_date}
            onChange={e => setDateRange(p => ({ ...p, end_date: e.target.value }))}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <button onClick={loadReports}
            className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium"
            style={{ backgroundColor: 'var(--accent-primary)', color: 'white' }}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* ── Financial Overview ── */}
      <div>
        <SectionHeading icon={DollarSign} title="Financial Overview" color="var(--accent-success)" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={TrendingUp}   label="Total Deposits"    color="var(--accent-success)"
            value={fmtUSD(deposits.total)}
            subtext={`${deposits.count || 0} transactions · ${deposits.unique_users || 0} users`} />
          <StatCard icon={TrendingDown} label="Total Withdrawals" color="var(--accent-danger)"
            value={fmtUSD(withdrawals.total)}
            subtext={`${withdrawals.count || 0} transactions · ${withdrawals.approval_rate || 0}% approved`} />
          <StatCard icon={DollarSign}   label="Net Cash Flow"
            color={financial?.net_cash_flow >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'}
            value={fmtUSD(financial?.net_cash_flow)}
            subtext={`${financial?.deposit_withdrawal_ratio || '0.00'}:1 dep/wd ratio`} />
          <StatCard icon={Users}        label="Total Users"       color="var(--accent-info)"
            value={users?.total_users || 0}
            subtext={`${users?.active_users || 0} active · ${users?.kyc_approved || 0} KYC verified`} />
        </div>
      </div>

      {/* ── Pending Requests ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: 'var(--accent-warning)' }} />
            <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Pending Requests</h2>
            {pendingRequests.length > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)', color: 'var(--accent-warning)' }}>
                {pendingRequests.length} waiting
              </span>
            )}
            {urgentCount > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)', color: 'var(--accent-danger)' }}>
                {urgentCount} urgent (2h+)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
            <span>{pendingDeposits.length} deposits</span>
            <span>·</span>
            <span>{pendingWithdrawals.length} withdrawals</span>
          </div>
        </div>

        <div className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          {pendingRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <CheckCircle className="w-8 h-8" style={{ color: 'var(--accent-success)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>All caught up!</p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>No pending requests at the moment</p>
            </div>
          ) : (
            <div className="px-4">
              {pendingRequests.map((r, i) => <PendingRow key={`${r.request_type}-${r.id}-${i}`} r={r} />)}
            </div>
          )}
        </div>
      </div>

      {/* ── Account Overview ── */}
      <div>
        <SectionHeading icon={BarChart2} title="Account Overview" color="var(--accent-primary)" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <MetricCard label="Total Accounts"    icon={Users}      value={trading?.total_accounts || 0} />
          <MetricCard label="Total Balance"     icon={DollarSign} value={fmtUSD(trading?.total_balance)}    color="var(--accent-success)" />
          <MetricCard label="Total Equity"      icon={TrendingUp} value={fmtUSD(trading?.total_equity)}     color="var(--accent-success)" />
          <MetricCard label="Total Credit"      icon={CreditCard} value={fmtUSD(trading?.total_credit_given)} />
          <MetricCard label="Margin Used"       icon={Activity}   value={fmtUSD(trading?.total_margin_used)} />
          <MetricCard label="Free Margin"       icon={Wallet}     value={fmtUSD(trading?.total_free_margin)} color="var(--accent-info)" />
          <MetricCard label="Open Positions"    icon={TrendingUp} value={trading?.total_open_positions || 0} />
          <MetricCard label="Unrealized P&L"    icon={TrendingUp}
            value={fmtUSD(trading?.total_unrealized_pnl)}
            color={trading?.total_unrealized_pnl >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'} />
        </div>
      </div>

      {/* ── Risk Summary ── */}
      {(trading?.accounts_at_stopout > 0 || trading?.accounts_at_margin_call > 0 || trading?.accounts_high_risk > 0) && (
        <div>
          <SectionHeading icon={AlertTriangle} title="Risk Alerts" color="var(--accent-danger)" />
          <div className="grid grid-cols-3 gap-3">
            <div className="p-4 rounded-xl border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-danger)' }}>Stop Out</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color: 'var(--accent-danger)' }}>{trading?.accounts_at_stopout || 0}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>accounts</p>
            </div>
            <div className="p-4 rounded-xl border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-warning) 25%, transparent)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>Margin Call</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color: 'var(--accent-warning)' }}>{trading?.accounts_at_margin_call || 0}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>accounts</p>
            </div>
            <div className="p-4 rounded-xl border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-warning) 25%, transparent)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>High Risk</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color: 'var(--accent-warning)' }}>{trading?.accounts_high_risk || 0}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>accounts</p>
            </div>
          </div>
        </div>
      )}

      {/* ── User Stats ── */}
      <div>
        <SectionHeading icon={Users} title="User Statistics" color="var(--accent-info)" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total Users"        icon={Users}      value={users?.total_users || 0} />
          <MetricCard label="Active Users"       icon={UserCheck}  value={users?.active_users || 0}    color="var(--accent-success)" />
          <MetricCard label="Email Verified"     icon={CheckCircle} value={users?.email_verified_users || 0} color="var(--accent-success)" />
          <MetricCard label="KYC Approved"       icon={Shield}     value={users?.kyc_approved || 0}    color="var(--accent-success)" />
          <MetricCard label="KYC Pending"        icon={Clock}      value={users?.kyc_pending || 0}     color="var(--accent-warning)" />
          <MetricCard label="KYC Rejected"       icon={XCircle}    value={users?.kyc_rejected || 0}    color="var(--accent-danger)" />
          <MetricCard label="Not Submitted"      icon={UserX}      value={users?.kyc_not_submitted || 0} />
          <MetricCard label="Active This Month"  icon={Activity}   value={users?.logged_in_this_month || 0} color="var(--accent-info)" />
        </div>
      </div>

      {/* ── Deposit Stats Detail ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={TrendingUp} title="Deposit Details" color="var(--accent-success)" />
          <div className="space-y-2">
            {[
              { label: 'Total Deposited',   value: fmtUSD(deposits.total) },
              { label: 'Total Count',       value: deposits.count || 0 },
              { label: 'Average Deposit',   value: fmtUSD(deposits.average) },
              { label: 'Largest Deposit',   value: fmtUSD(deposits.max) },
              { label: 'Smallest Deposit',  value: fmtUSD(deposits.min) },
              { label: 'Unique Depositors', value: deposits.unique_users || 0 },
              { label: 'Pending Amount',    value: fmtUSD(deposits.pending) },
              { label: 'Pending Count',     value: deposits.pending_count || 0 },
              { label: 'Approval Rate',     value: `${deposits.approval_rate || 0}%` },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-1.5 border-b last:border-0"
                style={{ borderColor: 'var(--border-color)' }}>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span className="text-sm font-semibold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={TrendingDown} title="Withdrawal Details" color="var(--accent-danger)" />
          <div className="space-y-2">
            {[
              { label: 'Total Withdrawn',      value: fmtUSD(withdrawals.total) },
              { label: 'Total Count',           value: withdrawals.count || 0 },
              { label: 'Average Withdrawal',    value: fmtUSD(withdrawals.average) },
              { label: 'Largest Withdrawal',    value: fmtUSD(withdrawals.max) },
              { label: 'Smallest Withdrawal',   value: fmtUSD(withdrawals.min) },
              { label: 'Unique Withdrawers',    value: withdrawals.unique_users || 0 },
              { label: 'Pending Amount',        value: fmtUSD(withdrawals.pending) },
              { label: 'Pending Count',         value: withdrawals.pending_count || 0 },
              { label: 'Approval Rate',         value: `${withdrawals.approval_rate || 0}%` },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-1.5 border-b last:border-0"
                style={{ borderColor: 'var(--border-color)' }}>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span className="text-sm font-semibold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Top Symbols ── */}
      {top_symbols && top_symbols.length > 0 && (
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={BarChart2} title="Top Symbols by Exposure" color="var(--accent-primary)" />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                  {['Symbol', 'Positions', 'Net Volume', 'Exposure', 'P&L'].map(h => (
                    <th key={h} className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top_symbols.map((s, i) => (
                  <tr key={i} className="border-b last:border-0" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="py-3 px-3 font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{s.symbol}</td>
                    <td className="py-3 px-3 font-mono text-sm" style={{ color: 'var(--text-primary)' }}>{s.position_count}</td>
                    <td className="py-3 px-3 font-mono text-sm"
                      style={{ color: parseFloat(s.net_volume) >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                      {parseFloat(s.net_volume).toFixed(2)}
                    </td>
                    <td className="py-3 px-3 font-mono text-sm" style={{ color: 'var(--text-primary)' }}>
                      {fmtUSD(s.net_exposure)}
                    </td>
                    <td className="py-3 px-3 font-mono text-sm"
                      style={{ color: parseFloat(s.total_pnl) >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                      {fmtUSD(s.total_pnl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Risky Accounts ── */}
      {riskyAccounts.length > 0 && (
        <div className="p-5 rounded-xl border"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={AlertTriangle} title="Risky Accounts" color="var(--accent-warning)" />
          <div className="space-y-2">
            {riskyAccounts.map((a) => (
              <div key={a.user_id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{a.name || a.email}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{a.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold"
                    style={{ color: ['STOP_OUT', 'MARGIN_CALL'].includes(a.risk_level) ? 'var(--accent-danger)' : 'var(--accent-warning)' }}>
                    {a.risk_level}
                  </p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                    Margin: {parseFloat(a.margin_level).toFixed(0)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}